import React from 'react';

// Design System Components
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';

// Types
import { EditingBannerProps } from '../../types';

/**
 * EditingBanner Component
 *
 * Displays a banner when edit mode is active with Cancel and Save buttons
 * Shows below the action bar during editing
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11625-61769
 */
const EditingBanner = ({
  onCancel,
  onSave,
  isSaving = false,
  className = '',
}: EditingBannerProps) => {
  return (
    <div
      /*
        `role="status"` (2026-08-31). Entering edit mode changes what every cell in
        the table below does, and the only announcement of it was a visual band. A
        polite live region says "Editing" when it appears, which is the one thing a
        screen-reader user was not being told.
      */
      role="status"
      data-testid="catalog-editing-banner"
      className={`tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-4 tw-py-1.5 tw-bg-surface-sunken tw-border-b tw-border-edge ${className}`}
    >
      {/*
        Was `variant="heading-2" className="tw-text-black"` — 18px/500 in pure #000,
        rendered as an <h3>. Two faults measured on the shipped stylesheet:

        - **#000 is not a design-system ink.** `tw-text-black` is Tailwind's own
          default, still in the palette but outside the `ink` ramp; the DS text ink
          is #172b4d. Pure black at 18px in a 49px strip is the heaviest type on the
          screen, for a label.
        - **A mode indicator is not a document heading.** `heading-2` maps to <h3>,
          so "Editing" was landing in the heading outline between the page title and
          the table's own headings, and a screen-reader user navigating by heading
          hit it as structure.

        `title-header` is 14px/600 — the same size as the Cancel/Save labels beside
        it, so the band reads as one line of type rather than a heading with two
        controls after it — and `as="span"` keeps it out of the outline. `role="status"`
        above is what now carries it to assistive tech, which is the right channel for
        a mode change.

        Deliberately NOT the small-caps eyebrow (12px/700 + `uppercase`) this first
        used: the table's own column headers are uppercase, and the strip sits
        directly on top of them.
      */}
      <Typography
        as="span"
        variant="title-header"
        className="tw-text-ink"
        data-testid="catalog-editing-banner-label"
      >
        Editing
      </Typography>

      {/*
        `sm` (28px), not the default `md` (36px). This is a contextual strip under a
        toolbar whose controls are already 36px — repeating that height made the band
        49px tall and read as a SECOND toolbar competing with the first. At `sm` the
        band is 41px and the two rows are legible as parent and child. `gap-2` matches
        the toolbar's own gap, which `gap-4` did not.
      */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
          data-testid="catalog-editing-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={isSaving}
          data-testid="catalog-editing-save-btn"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default EditingBanner;
